export function parseIdList(value) {
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter((id) => !Number.isNaN(id));
}

/** Assigned office agent ids from API (`branch` / `selected_branches` or `user_branches`). */
export function getAssignedBranchIds(user) {
  const fromBranch = parseIdList(user?.branch);
  if (fromBranch.length) return fromBranch;

  const fromSelected = parseIdList(user?.selected_branches);
  if (fromSelected.length) return fromSelected;

  if (Array.isArray(user?.user_branches) && user.user_branches.length) {
    if (user.user_branches.some((row) => row?.is_other)) {
      return [0];
    }
    return user.user_branches
      .map((row) => Number(row?.agent_id))
      .filter((id) => !Number.isNaN(id));
  }

  return [];
}

export function sortByName(list, key = "name") {
  return [...(list || [])].sort((a, b) =>
    String(a?.[key] || "").localeCompare(String(b?.[key] || ""), undefined, {
      sensitivity: "base",
    }),
  );
}

export function resolveBranchName(branchId, branches) {
  const branch = (branches || []).find((item) => Number(item.id) === Number(branchId));
  if (!branch) {
    if (Number(branchId) === 0) return "Others";
    return `Branch #${branchId}`;
  }
  return branch.name || branch.agencyname || `Branch #${branchId}`;
}

export function resolveBranchOption(branchId, branches) {
  const list = branches || [];
  const found = list.find((item) => Number(item.id) === Number(branchId));
  if (found) return found;
  if (Number(branchId) === 0) return { id: 0, name: "Others" };
  return { id: branchId, name: resolveBranchName(branchId, list) };
}

export function getUserBranchRows(user, branches) {
  const branchIds = getAssignedBranchIds(user);
  const showOnlyIds = parseIdList(user?.show_branch_only);

  if (!branchIds.length) {
    return [];
  }

  return branchIds.map((branchId) => ({
    branchId,
    branchName: resolveBranchName(branchId, branches),
    showOnly: showOnlyIds.includes(branchId),
  }));
}

/** Branch objects currently assigned to a user (for assign modal). */
export function getUserAssignedBranches(user, branches) {
  const branchIds = getAssignedBranchIds(user);
  return branchIds.map((branchId) => resolveBranchOption(branchId, branches));
}

export function getManagerId(row) {
  const id =
    row?.handling_manager ?? row?.handling_manager_id ?? row?.manager ?? "";
  return id != null && id !== "" ? String(id) : "";
}

export function getBranchDisplayName(row) {
  return row?.agencyname || row?.name || "—";
}

/** GET getAllUsers — supports flat array or paginated `{ data: [] }`. */
export function normalizeUsersListResponse(res) {
  const payload = res?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

export function formatUserBranchField(branches = []) {
  const ids = (branches || [])
    .map((branch) => Number(branch?.id ?? branch))
    .filter((id) => !Number.isNaN(id));
  return ids.join(",");
}

export function buildAssignBranchesPayload(userId, branches = []) {
  return {
    user: userId,
    branches: (branches || [])
      .map((branch) => ({
        id: Number(branch?.id ?? branch),
        name: branch?.name || branch?.agencyname || "",
      }))
      .filter((branch) => !Number.isNaN(branch.id)),
  };
}

export function extractSavedBranchField(response, fallbackBranches = []) {
  const fromRoot = response?.data?.branch;
  if (fromRoot !== undefined && fromRoot !== null && fromRoot !== "") {
    return String(fromRoot);
  }
  const fromUser = response?.data?.data?.branch ?? response?.data?.data?.selected_branches;
  if (fromUser !== undefined && fromUser !== null && fromUser !== "") {
    return String(fromUser);
  }
  return formatUserBranchField(fallbackBranches);
}
